import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const reputationItems = [
    { icon: 'ri-shield-star-line', title: t('about.rep_license'), desc: t('about.rep_license_desc') },
    { icon: 'ri-safe-2-line', title: t('about.rep_security'), desc: t('about.rep_security_desc') },
    { icon: 'ri-scales-3-line', title: t('about.rep_fair'), desc: t('about.rep_fair_desc') },
    { icon: 'ri-lock-star-line', title: t('about.rep_privacy'), desc: t('about.rep_privacy_desc') },
  ];

  const faqItems = [
    { q: '如何注册皇冠体育账号？', a: '点击网站右上角的"立即注册"按钮，填写手机号或邮箱，设置密码并完成验证即可注册成功。注册后建议立即完成实名认证，以便正常使用存取款功能。' },
    { q: '皇冠体育支持哪些支付方式？', a: '皇冠体育支持多种支付方式，包括银行卡转账、微信支付、支付宝、USDT数字货币等。所有支付方式均经过严格安全审核，确保资金安全。' },
    { q: '提现需要多长时间到账？', a: '皇冠体育承诺极速提现服务，一般情况下提现申请提交后5-30分钟内即可到账。具体到账时间取决于所选支付方式，高峰期可能会有微小延迟。' },
    { q: '竞猜赔率是如何计算的？', a: '皇冠体育的赔率由专业分析团队结合全球赛事数据、市场行情、球队状态等多维度因素综合计算得出，赔率实时更新，确保公平公正。' },
    { q: '如何联系客服？', a: '您可以通过以下方式联系我们的客服团队：在线客服（7×24小时）、客服热线 400-888-6666、客服邮箱 service@huangguan-sports.com。我们随时为您服务。' },
  ];

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('seo.about_title'),
    description: t('seo.about_desc'),
    url: 'https://huangguan-sports.com/about',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={t('seo.about_title')}
        description={t('seo.about_desc')}
        keywords={t('seo.about_keywords')}
        canonicalUrl="https://huangguan-sports.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="relative h-[280px] md:h-[360px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(https://readdy.ai/api/search-image?query=Luxurious%20modern%20corporate%20office%20headquarters%20interior%20with%20warm%20golden%20and%20dark%20green%20accents%2C%20professional%20business%20atmosphere%2C%20elegant%20reception%20area%20with%20marble%20textures%2C%20premium%20corporate%20design%2C%20warm%20ambient%20lighting%2C%20executive%20suite%20style&width=1920&height=500&seq=about-header-001&orientation=landscape)`,
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/70 to-foreground-900/60"></div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3">
                {t('about.title')}
              </h1>
              <p className="text-background-50/70 text-base max-w-xl">
                业内领先的专业体育竞猜平台，值得您信赖的体育娱乐首选
              </p>
            </div>
          </section>

          <div className="px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={[{ label: t('about.title') }]} />

              {/* Brand Introduction */}
              <section className="mb-16">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8">
                  {t('about.intro_title')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                  <div>
                    <p className="text-foreground-700 leading-relaxed mb-4">{t('about.intro_text1')}</p>
                    <p className="text-foreground-700 leading-relaxed mb-4">{t('about.intro_text2')}</p>
                    <p className="text-foreground-700 leading-relaxed">{t('about.intro_text3')}</p>
                  </div>
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20modern%20office%20workspace%20with%20multiple%20screens%20displaying%20sports%20data%20and%20analytics%2C%20professional%20team%20working%20in%20a%20premium%20sports%20media%20environment%2C%20warm%20golden%20lighting%2C%20luxurious%20corporate%20design%20with%20dark%20green%20accents%2C%20executive%20business%20atmosphere%2C%20clean%20modern%20aesthetic&width=800&height=600&seq=about-intro-001&orientation=squarish"
                      alt="皇冠体育办公环境"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </section>

              {/* Reputation */}
              <section className="mb-16">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  {t('about.reputation_title')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {reputationItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-background-50 rounded-xl p-6 border border-background-200 hover:border-accent-200 transition-all duration-300 text-center group cursor-default"
                    >
                      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-50 flex items-center justify-center group-hover:bg-accent-100 transition-colors">
                        <i className={`${item.icon} text-2xl text-accent-500`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{item.title}</h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  {t('about.contact_title')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">{t('about.contact_email')}</p>
                    <p className="font-medium text-foreground-800 text-sm">service@huangguan-sports.com</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">{t('about.contact_phone')}</p>
                    <p className="font-medium text-foreground-800 text-sm">400-888-6666</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                      <i className="ri-time-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">{t('about.contact_hours')}</p>
                    <p className="font-medium text-foreground-800 text-sm">7×24小时 全年无休</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                      <i className="ri-map-pin-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">{t('about.contact_address')}</p>
                    <p className="font-medium text-foreground-800 text-sm">国际金融中心 · 香港</p>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-16">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  {t('about.faq_title')}
                </h2>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqItems.map((item, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-50 transition-colors list-none">
                        <span className="font-medium text-foreground-800 pr-4">{item.q}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                      </summary>
                      <div className="px-6 pb-4 text-foreground-600 text-sm leading-relaxed">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}